// Sayfa çizim motoru: üç saydam katman PDF tuvalinin üstünde durur.
//   hl   — fosforlu kalem (CSS mix-blend-mode: multiply; yazılar fosforun altında okunur kalır)
//   ink  — tamamlanmış çizimler; yeni çizim gelince yalnızca o eklenir, silmede baştan çizilir
//   live — o an çizilenler: kendi kalemin, başkalarının canlı kalemi, şekil önizlemeleri, lazer
// Çizimler requestAnimationFrame ile kare başına bir kez yapılır.
import type { Annotation, InkAnnotation, InkTool, Point } from '../../shared/protocol';
import type { LaserTrail } from '../lib/lesson';
import { drawAnnotation, drawFigure, drawInk, drawShape, type ShapeDraw } from './draw';
import type { FigureAnnotation } from '../../shared/protocol';

export interface LocalLive {
  ink: { tool: InkTool; color: string; size: number; points: Point[] } | null;
  shape: ShapeDraw | null;
  /** Yerleştirilmeyi bekleyen formülün imleci izleyen önizlemesi. */
  figure: Pick<FigureAnnotation, 'x' | 'y' | 'texts' | 'lines' | 'color'> | null;
  eraser: { x: number; y: number; r: number } | null;
}

export interface RemoteLive {
  drafts: InkAnnotation[];
  previews: ShapeDraw[];
  lasers: LaserTrail[];
}

const MAX_CANVAS_PIXELS = 16_000_000; // iOS Safari tuval sınırı

export function effectiveDpr(cssWidth: number, cssHeight: number): number {
  const dpr = Math.min(window.devicePixelRatio || 1, 3);
  const limit = Math.sqrt(MAX_CANVAS_PIXELS / Math.max(1, cssWidth * cssHeight));
  return Math.max(0.5, Math.min(dpr, limit));
}

export class PageRenderer {
  page = 0;
  private scale = 1;
  private dpr = 1;
  private readonly ctx: { hl: CanvasRenderingContext2D; ink: CanvasRenderingContext2D; live: CanvasRenderingContext2D };
  private highlights: Annotation[] = [];
  private inks: Annotation[] = [];
  private painted = 0;
  private dirtyHl = true;
  private dirtyInk = true;
  private dirtyLive = true;
  private raf = 0;
  private destroyed = false;

  constructor(
    private readonly canvases: { hl: HTMLCanvasElement; ink: HTMLCanvasElement; live: HTMLCanvasElement },
    private readonly local: () => LocalLive,
    private readonly remote: (page: number) => RemoteLive,
  ) {
    const get = (c: HTMLCanvasElement) => {
      const ctx = c.getContext('2d');
      if (!ctx) throw new Error('Tarayıcı Canvas 2D desteklemiyor.');
      return ctx;
    };
    this.ctx = { hl: get(canvases.hl), ink: get(canvases.ink), live: get(canvases.live) };
  }

  configure(page: number, width: number, height: number, scale: number, dpr: number): void {
    const pw = Math.max(1, Math.round(width * scale * dpr));
    const ph = Math.max(1, Math.round(height * scale * dpr));
    for (const c of [this.canvases.hl, this.canvases.ink, this.canvases.live]) {
      if (c.width !== pw || c.height !== ph) {
        c.width = pw;
        c.height = ph;
      }
      c.style.width = `${width * scale}px`;
      c.style.height = `${height * scale}px`;
    }
    if (page !== this.page) {
      this.highlights = [];
      this.inks = [];
    }
    this.page = page;
    this.scale = scale;
    this.dpr = dpr;
    this.repaintAll();
  }

  setAnnotations(list: readonly Annotation[]): void {
    const hl: Annotation[] = [];
    const ink: Annotation[] = [];
    for (const a of list) (a.kind === 'ink' && a.tool === 'highlighter' ? hl : ink).push(a);

    let appendOnly = ink.length >= this.inks.length;
    for (let i = 0; appendOnly && i < this.inks.length; i++) if (ink[i] !== this.inks[i]) appendOnly = false;
    if (!appendOnly) this.dirtyInk = true;
    this.inks = ink;

    if (hl.length !== this.highlights.length || hl.some((a, i) => a !== this.highlights[i])) this.dirtyHl = true;
    this.highlights = hl;
    this.schedule();
  }

  invalidateLive(highlights = false): void {
    this.dirtyLive = true;
    if (highlights) this.dirtyHl = true;
    this.schedule();
  }

  repaintAll(): void {
    this.dirtyHl = this.dirtyInk = this.dirtyLive = true;
    this.schedule();
  }

  destroy(): void {
    this.destroyed = true;
    if (this.raf) cancelAnimationFrame(this.raf);
  }

  private schedule() {
    if (!this.raf && !this.destroyed) this.raf = requestAnimationFrame(this.frame);
  }

  private reset(ctx: CanvasRenderingContext2D) {
    const k = this.scale * this.dpr;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    ctx.setTransform(k, 0, 0, k, 0, 0);
  }

  private frame = () => {
    this.raf = 0;
    if (this.destroyed || !this.page) return;

    if (this.dirtyHl) {
      this.dirtyHl = false;
      const ctx = this.ctx.hl;
      this.reset(ctx);
      for (const a of this.highlights) drawAnnotation(ctx, a);
      for (const d of this.remote(this.page).drafts) if (d.tool === 'highlighter') drawInk(ctx, d.tool, d.color, d.size, d.points);
      const own = this.local().ink;
      if (own && own.tool === 'highlighter') drawInk(ctx, own.tool, own.color, own.size, own.points);
    }

    const ink = this.ctx.ink;
    if (this.dirtyInk) {
      this.dirtyInk = false;
      this.painted = 0;
      this.reset(ink);
    }
    if (this.painted < this.inks.length) {
      const k = this.scale * this.dpr;
      ink.setTransform(k, 0, 0, k, 0, 0);
      for (let i = this.painted; i < this.inks.length; i++) drawAnnotation(ink, this.inks[i]);
      this.painted = this.inks.length;
    }

    if (this.dirtyLive) {
      this.dirtyLive = false;
      if (this.paintLive(Date.now())) {
        this.dirtyLive = true; // lazer izi sönümlenirken animasyona devam et
        this.schedule();
      }
    }
  };

  private paintLive(now: number): boolean {
    const ctx = this.ctx.live;
    this.reset(ctx);
    const remote = this.remote(this.page);
    const local = this.local();

    for (const d of remote.drafts) if (d.tool === 'pen') drawInk(ctx, d.tool, d.color, d.size, d.points);
    if (remote.previews.length) {
      ctx.globalAlpha = 0.85;
      for (const p of remote.previews) drawShape(ctx, p);
      ctx.globalAlpha = 1;
    }
    if (local.ink && local.ink.tool === 'pen') drawInk(ctx, local.ink.tool, local.ink.color, local.ink.size, local.ink.points);
    if (local.shape) drawShape(ctx, local.shape);
    if (local.figure) {
      ctx.globalAlpha = 0.55;
      drawFigure(ctx, local.figure);
      ctx.globalAlpha = 1;
    }
    for (const d of remote.drafts) {
      const last = d.points[d.points.length - 1];
      if (last) this.nameTag(ctx, d.authorName, last[0], last[1]);
    }

    let animating = false;
    for (const l of remote.lasers) if (this.laser(ctx, l, now)) animating = true;

    if (local.eraser) {
      const s = 1 / this.scale;
      ctx.lineWidth = s;
      ctx.strokeStyle = 'rgba(15, 36, 67, 0.75)';
      ctx.fillStyle = 'rgba(255, 255, 255, 0.35)';
      ctx.setLineDash([4 * s, 3 * s]);
      ctx.beginPath();
      ctx.arc(local.eraser.x, local.eraser.y, local.eraser.r, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      ctx.setLineDash([]);
    }
    return animating;
  }

  /** Çizen kişinin adı, kalemin ucunda; ekranda sabit boyutta. */
  private nameTag(ctx: CanvasRenderingContext2D, name: string, x: number, y: number) {
    const s = 1 / this.scale;
    ctx.font = `600 ${11 * s}px system-ui, -apple-system, "Segoe UI", sans-serif`;
    const w = ctx.measureText(name).width + 12 * s;
    const h = 18 * s;
    const left = x + 8 * s;
    const top = y - h - 6 * s;
    ctx.fillStyle = 'rgba(10, 16, 94, 0.88)';
    ctx.beginPath();
    if (typeof ctx.roundRect === 'function') ctx.roundRect(left, top, w, h, 5 * s);
    else ctx.rect(left, top, w, h); // Safari < 16
    ctx.fill();
    ctx.fillStyle = '#ffffff';
    ctx.textBaseline = 'middle';
    ctx.fillText(name, left + 6 * s, top + h / 2);
    ctx.textBaseline = 'alphabetic';
  }

  private laser(ctx: CanvasRenderingContext2D, l: LaserTrail, now: number): boolean {
    const age = now - l.last;
    if (age > 1500) return false;
    const s = 1 / this.scale;
    const fade = age < 900 ? 1 : 1 - (age - 900) / 600;
    const pts = l.points.filter((p) => now - p.t < 450);
    ctx.lineCap = 'round';
    for (let i = 1; i < pts.length; i++) {
      const life = 1 - (now - pts[i].t) / 450;
      ctx.strokeStyle = `rgba(239, 68, 68, ${0.5 * life * fade})`;
      ctx.lineWidth = 7 * s * Math.max(0.2, life);
      ctx.beginPath();
      ctx.moveTo(pts[i - 1].x, pts[i - 1].y);
      ctx.lineTo(pts[i].x, pts[i].y);
      ctx.stroke();
    }
    const head = l.points[l.points.length - 1];
    if (head) {
      ctx.save();
      ctx.globalAlpha = fade;
      ctx.shadowColor = 'rgba(239, 68, 68, 0.9)';
      ctx.shadowBlur = 14;
      ctx.fillStyle = '#ef4444';
      ctx.beginPath();
      ctx.arc(head.x, head.y, 6.5 * s, 0, Math.PI * 2);
      ctx.fill();
      ctx.shadowBlur = 0;
      ctx.fillStyle = '#fff5f5';
      ctx.beginPath();
      ctx.arc(head.x, head.y, 2.2 * s, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
    return true;
  }
}
